import { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  Container, Dialog, DialogContent, FormControl, InputLabel,
  MenuItem, Select, Stack, Typography,
} from '@mui/material';
import Iconify from '../../components/iconify';
import { useProject } from '../../hooks/ProjectContext';

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function extractOperator(notes) {
  if (!notes) return null;
  const m = notes.match(/captured by ([^\n—]+)/i);
  return m ? m[1].trim() : null;
}

function bearerHeaders() {
  const tkn = localStorage.getItem('tkn');
  return { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) };
}

export default function EvidenceReviewPage() {
  const { selectedProject, selectedSite } = useProject();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [taskFilter, setTaskFilter] = useState('');
  const [reviewing, setReviewing] = useState({});
  const [lightbox, setLightbox] = useState(null);
  const [error, setError] = useState(null);

  const projectId = selectedProject?.code || '';
  const siteId = selectedSite?.code || '';

  const load = useCallback(() => {
    if (!projectId) { setItems([]); return; }
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ project: projectId, excludeTest: '1', limit: '200' });
    if (siteId) params.set('site', siteId);
    if (filter === 'pending') params.set('reviewed', '0');
    if (filter === 'reviewed') params.set('reviewed', '1');
    fetch(`/v1/evidence?${params}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Failed to load')))
      .then(rows => setItems(Array.isArray(rows) ? rows : []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [projectId, siteId, filter]);

  useEffect(() => { load(); }, [load]);

  async function handleReview(id, undo) {
    setReviewing(s => ({ ...s, [id]: true }));
    try {
      const r = await fetch(`/v1/evidence/${encodeURIComponent(id)}/review`, {
        method: 'PATCH',
        headers: bearerHeaders(),
        body: JSON.stringify(undo ? { undo: true } : {}),
      });
      if (!r.ok) throw new Error('Review failed');
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setReviewing(s => ({ ...s, [id]: false }));
    }
  }

  const tasks = [...new Set(items.map(e => e.task_label).filter(Boolean))].sort();
  const filtered = taskFilter ? items.filter(e => e.task_label === taskFilter) : items;

  return (
    <>
      <Helmet><title>Evidence Review | TERRRA</title></Helmet>
      <Container maxWidth="md" sx={{ py: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4">Evidence Review</Typography>
            <Typography variant="body2" color="text.secondary">
              {projectId ? <>Review incoming evidence for <strong>{projectId}</strong></> : 'Select a project'}
            </Typography>
          </Box>
          <Button size="small" variant="outlined" onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filter} label="Status" onChange={e => setFilter(e.target.value)}>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="reviewed">Reviewed</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
          {tasks.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Task</InputLabel>
              <Select value={taskFilter} label="Task" onChange={e => setTaskFilter(e.target.value)}>
                <MenuItem value="">All tasks</MenuItem>
                {tasks.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {filtered.length} item{filtered.length !== 1 ? 's' : ''}
          </Typography>
        </Stack>

        {loading && <CircularProgress size={28} sx={{ display: 'block', mx: 'auto', my: 4 }} />}

        {!loading && filtered.length === 0 && projectId && (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                {filter === 'pending' ? 'No unreviewed evidence. You are up to date.' : 'No items match the filter.'}
              </Typography>
            </CardContent>
          </Card>
        )}

        <Stack spacing={1.5}>
          {filtered.map(ev => {
            const operator = extractOperator(ev.notes);
            const isReviewed = !!ev.reviewed_at;
            const busy = reviewing[ev.id];
            const noteLines = (ev.notes || '').split('\n').filter(l => !l.startsWith('— captured by') && !l.startsWith('— note edited') && l.trim());
            const userNote = noteLines.length > 0 ? noteLines[0] : null;
            return (
              <Card key={ev.id} sx={{ opacity: isReviewed && filter === 'all' ? 0.7 : 1 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 72, height: 72, borderRadius: 1, overflow: 'hidden',
                        flexShrink: 0, bgcolor: 'grey.200', cursor: 'pointer',
                      }}
                      onClick={() => setLightbox(ev.id)}
                    >
                      <img
                        src={`/v1/evidence/${ev.id}/thumb`}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={e => { e.target.src = `/v1/evidence/${ev.id}/image`; }}
                      />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
                        {ev.task_label && <Chip label={ev.task_label} size="small" variant="outlined" />}
                        {ev.task_id && (
                          <Typography variant="caption" color="text.secondary">{ev.task_id}</Typography>
                        )}
                        {ev.sampling_point && (
                          <Chip label={ev.sampling_point} size="small" color="info" variant="outlined" />
                        )}
                      </Stack>

                      <Typography variant="body2" fontWeight={600} noWrap>
                        {ev.title}
                      </Typography>

                      <Typography variant="caption" color="text.secondary">
                        {fmtDate(ev.captured_at)}
                        {operator && <> &middot; <strong>{operator}</strong></>}
                        {ev.site_id && <> &middot; {ev.site_id}</>}
                      </Typography>

                      {userNote && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                          {userNote.length > 120 ? userNote.substring(0, 120) + '...' : userNote}
                        </Typography>
                      )}

                      {isReviewed && (
                        <Typography variant="caption" color="success.main" sx={{ mt: 0.25, display: 'block' }}>
                          Reviewed {fmtDate(ev.reviewed_at)} by {ev.reviewed_by}
                        </Typography>
                      )}
                    </Box>

                    <Stack spacing={0.5} sx={{ flexShrink: 0, pt: 0.5 }}>
                      {!isReviewed ? (
                        <Button
                          size="small" variant="contained" color="success"
                          disabled={busy}
                          onClick={() => handleReview(ev.id, false)}
                          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <Iconify icon="eva:checkmark-fill" />}
                        >
                          Review
                        </Button>
                      ) : (
                        <Button
                          size="small" variant="outlined" color="inherit"
                          disabled={busy}
                          onClick={() => handleReview(ev.id, true)}
                        >
                          Undo
                        </Button>
                      )}
                      <Button
                        size="small" variant="outlined"
                        href={`/fieldmap?project=${encodeURIComponent(ev.project_id)}&site=${encodeURIComponent(ev.site_id || '')}&evidence=${encodeURIComponent(ev.id)}`}
                        target="_blank"
                        startIcon={<Iconify icon="eva:map-fill" />}
                      >
                        Map
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </Container>

      <Dialog open={!!lightbox} onClose={() => setLightbox(null)} maxWidth="md">
        <DialogContent sx={{ p: 0.5 }}>
          {lightbox && (
            <img
              src={`/v1/evidence/${lightbox}/image`}
              alt=""
              style={{ maxWidth: '100%', maxHeight: '80vh', display: 'block' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
